import { Edit } from "@mui/icons-material";
import { Divider } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { GlobalDataContext } from "../../../../../../contexts/GlobalData";
import UserProvider from "../../../../../../contexts/UserProvider";
import { ADD_PET } from "../../../../../../enums/ApiPaths";
import { EDIT_PET } from "../../../../../../enums/PermissionTitles";
import {
  PETS,
  PETS_DROP_DOWN,
  PETS_GENERAL,
} from "../../../../../../enums/QueryKeys";
import { PET_VIEW_USER_PARAM } from "../../../../../../enums/UrlPaths";
import usePut from "../../../../../../hooks/usePut";
import EditPetForm from "../../../../../pets/form/EditPetForm";
import useCanUserEditPet from "../../../../../pets/hooks/useCanUserEditPet";
import { normalizePetGender } from "../../../../../pets/utils/Functions";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import DialogCore from "../../../../components/core/DialogCore";

const formId = "edit_pet_form";

function getDefaultValues(pet) {
  return {
    ...pet,
    birthdate: pet?.birthdate ? dayjs(pet.birthdate, "DD/MM/YYYY") : null,
  };
}

function buildPetPayload(values) {
  const payload = {
    ...values,
    gender: normalizePetGender(values?.gender),
  };

  if (values?.user) {
    payload.ownerId = values.user.id;
  }

  if (values?.birthdate) {
    payload.birthdate = dayjs(values.birthdate).format("DD/MM/YYYY");
  }

  return payload;
}

// Actions-dialog row that opens the pet edit form; renders nothing without edit rights
export default function EditPetDialog({ pet = {} }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const canUserEditPet = useCanUserEditPet(pet);
  const [savedCount, setSavedCount] = React.useState(0);
  const [open, setOpen] = React.useState(false);

  const key = PETS_GENERAL + pet?.id;
  // The path id is only used by the backend when creating a pet, but it must never be "undefined"
  const ownerId =
    pet?.userId ?? searchParams.get(PET_VIEW_USER_PARAM) ?? pet?.clinicId;
  const put = usePut(ADD_PET + ownerId, key, [key, PETS, PETS_DROP_DOWN]);

  const methods = useForm({ defaultValues: getDefaultValues(pet) });
  const { reset } = methods;

  React.useEffect(() => {
    reset(getDefaultValues(pet));
  }, [pet, reset]);

  const canEdit =
    isPermissionAllowed(EDIT_PET) &&
    canUserEditPet &&
    pet?.active &&
    !pet?.deceased;

  if (!canEdit) return null;

  async function submitData(values) {
    const saved = await put.submitCallback(buildPetPayload(values));
    if (saved) setSavedCount((count) => count + 1);
  }

  return (
    <FormProvider {...methods}>
      <Divider />
      <ActionRow
        icon={<Edit />}
        label={t("EditPet")}
        description={t("EditPetActionDescription")}
        onClick={() => setOpen(true)}
        testId="pet.header.actions.edit"
      />
      <form id={formId} onSubmit={methods.handleSubmit(submitData)}>
        <DialogCore
          open={open}
          onClose={() => setOpen(false)}
          formId={formId}
          fontWeight={800}
          maxWidth="md"
          title={t("EditPetsInformation")}
          isLoading={put.loading}
          closeWhen={savedCount}
        >
          {/* Inside the dialog so the user list is only fetched once it's opened */}
          <UserProvider>
            <EditPetForm mutationKey={key} />
          </UserProvider>
        </DialogCore>
      </form>
    </FormProvider>
  );
}
